import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../Icon/Icon';
import {
  GROWTH_SETTINGS_GROUP_ORDER,
  GROWTH_SETTINGS_META,
  GROWTH_SETTINGS_NAV,
} from '../../growth-settings/config';
import {
  ST_INITIAL_BUDGET_VALUES,
  ST_INITIAL_PROFILE_VALUES,
} from '../../growth-settings/mockData';
import type {
  StBrandToneId,
  StBudgetFieldId,
  StConnectionItem,
  StDoctorItem,
  StNavItem,
  StPracticeProfileFieldId,
  StTabId,
  StToggleItem,
} from '../../growth-settings/types';
import { SettingsAutopilot } from './SettingsAutopilot';
import { SettingsBudget } from './SettingsBudget';
import { SettingsConnections } from './SettingsConnections';
import { SettingsContent } from './SettingsContent';
import { SettingsNotifications } from './SettingsNotifications';
import { SettingsPracticeProfile } from './SettingsPracticeProfile';
import { SettingsTargetArea } from './SettingsTargetArea';
import './growth-settings.css';

const EMPTY_EXCLUDED_POSTAL_CODES: string[] = [];

function syncFromProp<T>(prev: T, next: T): T {
  return recordsEqual(prev, next) ? prev : next;
}

function groupNav(items: readonly StNavItem[]) {
  const buckets = new Map<string, StNavItem[]>();
  for (const item of items) {
    const list = buckets.get(item.group) ?? [];
    list.push(item);
    buckets.set(item.group, list);
  }
  return GROWTH_SETTINGS_GROUP_ORDER.filter((group) => buckets.has(group)).map((group) => ({
    group,
    items: buckets.get(group) as StNavItem[],
  }));
}

function togglesToRecord(toggles: StToggleItem[]): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const toggle of toggles) {
    if (toggle.key) out[toggle.key] = toggle.enabled;
  }
  return out;
}

function recordsEqual(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function isTabDirty(
  tab: StTabId,
  state: {
    budgetValues: typeof ST_INITIAL_BUDGET_VALUES;
    initialBudgetValues: typeof ST_INITIAL_BUDGET_VALUES;
    profileValues: typeof ST_INITIAL_PROFILE_VALUES;
    initialProfileValues: typeof ST_INITIAL_PROFILE_VALUES;
    autopilotRules: Record<string, boolean>;
    initialAutopilotRules: Record<string, boolean>;
    notifications: Record<string, boolean>;
    initialNotifications: Record<string, boolean>;
    targetRadiusMiles: number;
    initialTargetRadiusMiles: number;
    competitorScanRadiusKm: number;
    initialCompetitorScanRadiusKm: number;
    excludedPostalCodes: string[];
    initialExcludedPostalCodes: string[];
    selectedTone: StBrandToneId;
    initialBrandTone: StBrandToneId;
    spanishContent: boolean;
    initialSpanishContent: boolean;
  },
): boolean {
  switch (tab) {
    case 'budget-guardrails':
      return !recordsEqual(state.budgetValues, state.initialBudgetValues);
    case 'practice-profile':
      return !recordsEqual(state.profileValues, state.initialProfileValues);
    case 'target-area':
      return (
        state.targetRadiusMiles !== state.initialTargetRadiusMiles ||
        state.competitorScanRadiusKm !== state.initialCompetitorScanRadiusKm ||
        !recordsEqual(state.excludedPostalCodes, state.initialExcludedPostalCodes)
      );
    case 'autopilot-rules':
      return !recordsEqual(state.autopilotRules, state.initialAutopilotRules);
    case 'notifications':
      return !recordsEqual(state.notifications, state.initialNotifications);
    case 'content-preferences':
      return state.selectedTone !== state.initialBrandTone || state.spanishContent !== state.initialSpanishContent;
    default:
      return false;
  }
}

function tabDirtyCount(tab: StTabId, state: Parameters<typeof isTabDirty>[1]): number {
  switch (tab) {
    case 'budget-guardrails': {
      let count = 0;
      for (const key of Object.keys(state.budgetValues) as StBudgetFieldId[]) {
        if (state.budgetValues[key] !== state.initialBudgetValues[key]) count += 1;
      }
      return count;
    }
    case 'practice-profile': {
      let count = 0;
      for (const key of Object.keys(state.profileValues) as StPracticeProfileFieldId[]) {
        if (state.profileValues[key] !== state.initialProfileValues[key]) count += 1;
      }
      return count;
    }
    case 'target-area': {
      let count = 0;
      if (state.targetRadiusMiles !== state.initialTargetRadiusMiles) count += 1;
      if (state.competitorScanRadiusKm !== state.initialCompetitorScanRadiusKm) count += 1;
      if (!recordsEqual(state.excludedPostalCodes, state.initialExcludedPostalCodes)) count += 1;
      return count;
    }
    case 'autopilot-rules': {
      let count = 0;
      for (const key of Object.keys(state.autopilotRules)) {
        if (state.autopilotRules[key] !== state.initialAutopilotRules[key]) count += 1;
      }
      return count;
    }
    case 'notifications': {
      let count = 0;
      for (const key of Object.keys(state.notifications)) {
        if (state.notifications[key] !== state.initialNotifications[key]) count += 1;
      }
      return count;
    }
    case 'content-preferences': {
      let count = 0;
      if (state.selectedTone !== state.initialBrandTone) count += 1;
      if (state.spanishContent !== state.initialSpanishContent) count += 1;
      return count;
    }
    default:
      return 0;
  }
}

function buildTabSavePayload(
  tab: StTabId,
  state: {
    budgetValues: typeof ST_INITIAL_BUDGET_VALUES;
    profileValues: typeof ST_INITIAL_PROFILE_VALUES;
    autopilotRules: Record<string, boolean>;
    notifications: Record<string, boolean>;
    targetRadiusMiles: number;
    competitorScanRadiusKm: number;
    excludedPostalCodes: string[];
    selectedTone: StBrandToneId;
    spanishContent: boolean;
  },
): Partial<GrowthSettingsSavePayload> {
  switch (tab) {
    case 'budget-guardrails':
      return { budget: state.budgetValues };
    case 'practice-profile':
      return { profile: state.profileValues };
    case 'target-area':
      return {
        targetRadiusMiles: state.targetRadiusMiles,
        competitorScanRadiusKm: state.competitorScanRadiusKm,
        excludedPostalCodes: state.excludedPostalCodes,
      };
    case 'autopilot-rules':
      return { autopilotRules: state.autopilotRules };
    case 'notifications':
      return { notifications: state.notifications };
    case 'content-preferences':
      return { brandTone: state.selectedTone, spanishContent: state.spanishContent };
    default:
      return {};
  }
}

export interface GrowthSettingsSavePayload {
  budget: typeof ST_INITIAL_BUDGET_VALUES;
  profile: typeof ST_INITIAL_PROFILE_VALUES;
  autopilotRules: Record<string, boolean>;
  notifications: Record<string, boolean>;
  targetRadiusMiles: number;
  competitorScanRadiusKm: number;
  excludedPostalCodes: string[];
  brandTone: StBrandToneId;
  spanishContent: boolean;
}

export interface GrowthSettingsSaveBarState {
  isDirty: boolean;
  dirtyCount: number;
  visibleTab: StTabId;
  save: () => void;
  discard: () => void;
}

export interface GrowthSettingsViewProps {
  initialBudgetValues?: typeof ST_INITIAL_BUDGET_VALUES;
  initialProfileValues?: typeof ST_INITIAL_PROFILE_VALUES;
  doctors?: StDoctorItem[];
  connections?: StConnectionItem[];
  autopilotToggles?: StToggleItem[];
  notificationToggles?: StToggleItem[];
  targetRadiusMiles?: number;
  competitorScanRadiusKm?: number;
  excludedPostalCodes?: string[];
  initialBrandTone?: StBrandToneId;
  spanishContent?: boolean;
  activeTab?: StTabId;
  loading?: boolean;
  onSave?: (payload: Partial<GrowthSettingsSavePayload>) => void;
  onConnect?: (provider: string) => void;
  onDisconnect?: (provider: string) => void;
  onSaveBarStateChange?: (state: GrowthSettingsSaveBarState) => void;
  onTabChange?: (tab: StTabId) => void;
}

export function GrowthSettingsView({
  initialBudgetValues = ST_INITIAL_BUDGET_VALUES,
  initialProfileValues = ST_INITIAL_PROFILE_VALUES,
  doctors,
  connections,
  autopilotToggles,
  notificationToggles,
  targetRadiusMiles: initialTargetRadiusMiles = 8,
  competitorScanRadiusKm: initialCompetitorScanRadiusKm = 20,
  excludedPostalCodes: initialExcludedPostalCodes = EMPTY_EXCLUDED_POSTAL_CODES,
  initialBrandTone = 'friendly-warm',
  spanishContent: initialSpanishContent = false,
  activeTab: routeTab = 'practice-profile',
  loading = false,
  onSave,
  onConnect,
  onDisconnect,
  onSaveBarStateChange,
  onTabChange,
}: GrowthSettingsViewProps = {}) {
  const meta = GROWTH_SETTINGS_META;
  const [visibleTab, setVisibleTab] = useState<StTabId>(routeTab);
  const [budgetValues, setBudgetValues] = useState(initialBudgetValues);
  const [profileValues, setProfileValues] = useState(initialProfileValues);
  const [autopilotRules, setAutopilotRules] = useState<Record<string, boolean>>(() =>
    togglesToRecord(autopilotToggles ?? []),
  );
  const [notifications, setNotifications] = useState<Record<string, boolean>>(() =>
    togglesToRecord(notificationToggles ?? []),
  );
  const [targetRadiusMiles, setTargetRadiusMiles] = useState(initialTargetRadiusMiles);
  const [competitorScanRadiusKm, setCompetitorScanRadiusKm] = useState(initialCompetitorScanRadiusKm);
  const [excludedPostalCodes, setExcludedPostalCodes] = useState(initialExcludedPostalCodes);
  const [selectedTone, setSelectedTone] = useState<StBrandToneId>(initialBrandTone);
  const [spanishContent, setSpanishContent] = useState(initialSpanishContent);

  useEffect(() => {
    setVisibleTab((prev) => (prev === routeTab ? prev : routeTab));
  }, [routeTab]);

  useEffect(() => {
    setBudgetValues((prev) => syncFromProp(prev, initialBudgetValues));
  }, [initialBudgetValues]);

  useEffect(() => {
    setProfileValues((prev) => syncFromProp(prev, initialProfileValues));
  }, [initialProfileValues]);

  useEffect(() => {
    if (!autopilotToggles) return;
    const next = togglesToRecord(autopilotToggles);
    setAutopilotRules((prev) => syncFromProp(prev, next));
  }, [autopilotToggles]);

  useEffect(() => {
    if (!notificationToggles) return;
    const next = togglesToRecord(notificationToggles);
    setNotifications((prev) => syncFromProp(prev, next));
  }, [notificationToggles]);

  useEffect(() => {
    setTargetRadiusMiles((prev) =>
      prev === initialTargetRadiusMiles ? prev : initialTargetRadiusMiles,
    );
  }, [initialTargetRadiusMiles]);

  useEffect(() => {
    setCompetitorScanRadiusKm((prev) =>
      prev === initialCompetitorScanRadiusKm ? prev : initialCompetitorScanRadiusKm,
    );
  }, [initialCompetitorScanRadiusKm]);

  useEffect(() => {
    setExcludedPostalCodes((prev) => syncFromProp(prev, initialExcludedPostalCodes));
  }, [initialExcludedPostalCodes]);

  useEffect(() => {
    setSelectedTone((prev) => (prev === initialBrandTone ? prev : initialBrandTone));
  }, [initialBrandTone]);

  useEffect(() => {
    setSpanishContent((prev) =>
      prev === initialSpanishContent ? prev : initialSpanishContent,
    );
  }, [initialSpanishContent]);

  const grouped = useMemo(() => groupNav(GROWTH_SETTINGS_NAV), []);

  const initialAutopilotRules = useMemo(
    () => togglesToRecord(autopilotToggles ?? []),
    [autopilotToggles],
  );
  const initialNotifications = useMemo(
    () => togglesToRecord(notificationToggles ?? []),
    [notificationToggles],
  );

  const tabState = useMemo(
    () => ({
      budgetValues,
      initialBudgetValues,
      profileValues,
      initialProfileValues,
      autopilotRules,
      initialAutopilotRules,
      notifications,
      initialNotifications,
      targetRadiusMiles,
      initialTargetRadiusMiles,
      competitorScanRadiusKm,
      initialCompetitorScanRadiusKm,
      excludedPostalCodes,
      initialExcludedPostalCodes,
      selectedTone,
      initialBrandTone,
      spanishContent,
      initialSpanishContent,
    }),
    [
      autopilotRules,
      budgetValues,
      competitorScanRadiusKm,
      excludedPostalCodes,
      initialAutopilotRules,
      initialBrandTone,
      initialBudgetValues,
      initialCompetitorScanRadiusKm,
      initialExcludedPostalCodes,
      initialNotifications,
      initialProfileValues,
      initialSpanishContent,
      initialTargetRadiusMiles,
      notifications,
      profileValues,
      selectedTone,
      spanishContent,
      targetRadiusMiles,
    ],
  );

  const isDirty = useMemo(
    () => isTabDirty(visibleTab, tabState),
    [tabState, visibleTab],
  );

  const dirtyCount = useMemo(
    () => tabDirtyCount(visibleTab, tabState),
    [tabState, visibleTab],
  );

  const handleSelectTab = useCallback(
    (tab: StTabId) => {
      if (tab === routeTab) return;
      setVisibleTab(tab);
      onTabChange?.(tab);
    },
    [onTabChange, routeTab],
  );

  const handleBudgetChange = (id: StBudgetFieldId, value: string) =>
    setBudgetValues((prev) => ({ ...prev, [id]: value }));

  const handleProfileChange = (id: StPracticeProfileFieldId, value: string) =>
    setProfileValues((prev) => ({ ...prev, [id]: value }));

  const handleAutopilotToggle = (key: string, enabled: boolean) =>
    setAutopilotRules((prev) => ({ ...prev, [key]: enabled }));

  const handleNotificationToggle = (key: string, enabled: boolean) =>
    setNotifications((prev) => ({ ...prev, [key]: enabled }));

  const handleDiscard = useCallback(() => {
    switch (visibleTab) {
      case 'budget-guardrails':
        setBudgetValues(initialBudgetValues);
        break;
      case 'practice-profile':
        setProfileValues(initialProfileValues);
        break;
      case 'target-area':
        setTargetRadiusMiles(initialTargetRadiusMiles);
        setCompetitorScanRadiusKm(initialCompetitorScanRadiusKm);
        setExcludedPostalCodes(initialExcludedPostalCodes);
        break;
      case 'autopilot-rules':
        setAutopilotRules(initialAutopilotRules);
        break;
      case 'notifications':
        setNotifications(initialNotifications);
        break;
      case 'content-preferences':
        setSelectedTone(initialBrandTone);
        setSpanishContent(initialSpanishContent);
        break;
      default:
        break;
    }
  }, [
    initialAutopilotRules,
    initialBrandTone,
    initialBudgetValues,
    initialCompetitorScanRadiusKm,
    initialExcludedPostalCodes,
    initialNotifications,
    initialProfileValues,
    initialSpanishContent,
    initialTargetRadiusMiles,
    visibleTab,
  ]);

  const handleSave = useCallback(() => {
    onSave?.(buildTabSavePayload(visibleTab, tabState));
  }, [onSave, tabState, visibleTab]);

  useEffect(() => {
    onSaveBarStateChange?.({
      isDirty,
      dirtyCount,
      visibleTab,
      save: handleSave,
      discard: handleDiscard,
    });
  }, [dirtyCount, handleDiscard, handleSave, isDirty, onSaveBarStateChange, visibleTab]);

  const autopilotUiToggles = useMemo(
    () =>
      (autopilotToggles ?? []).map((rule) => ({
        ...rule,
        enabled: rule.key ? (autopilotRules[rule.key] ?? rule.enabled) : rule.enabled,
      })),
    [autopilotRules, autopilotToggles],
  );

  const notificationUiToggles = useMemo(
    () =>
      (notificationToggles ?? []).map((rule) => ({
        ...rule,
        enabled: rule.key ? (notifications[rule.key] ?? rule.enabled) : rule.enabled,
      })),
    [notificationToggles, notifications],
  );

  const sectionSaveFooter = useMemo(
    () =>
      visibleTab !== 'connections'
        ? {
            onSave: handleSave,
            onDiscard: handleDiscard,
            loading,
            saveDisabled: !isDirty,
            showDiscard: isDirty,
          }
        : undefined,
    [handleDiscard, handleSave, isDirty, loading, visibleTab],
  );

  const saveFooterProps = sectionSaveFooter ? { saveFooter: sectionSaveFooter } : {};

  return (
    <div className="growth-settings-root relative flex min-h-0 flex-1">
      <nav
        className="gs-nav sticky top-0 flex h-full min-h-0 w-[244px] shrink-0 flex-col gap-[22px] overflow-y-auto border-r border-[var(--cream-2)] bg-[var(--cream-0)] py-[26px]"
        aria-label="Settings sections"
      >
        <div className="px-5">
          <div className="mb-1 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--ink-4)]">
            {meta.eyebrow}
          </div>
          <h2 className="m-0 font-[var(--font-display)] text-[22px] font-medium leading-tight tracking-[-0.025em] text-[var(--ink-1)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h2>
        </div>

        {grouped.map(({ group, items }) => (
          <div key={group}>
            <div className="px-5 pb-1.5 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.16em] text-[var(--ink-4)]">
              {group}
            </div>
            <div className="flex flex-col px-2">
              {items.map((item) => {
                const active = visibleTab === item.id;
                const navClassName = [
                  'mb-px flex w-full cursor-pointer items-center gap-2.5 rounded-[var(--r-sm)] border-0 px-3 py-[9px] text-left text-[13px]',
                  active
                    ? 'bg-[var(--surface-mute)] font-semibold text-[var(--ink-0)]'
                    : 'bg-transparent font-medium text-[var(--ink-2)] hover:bg-[var(--cream-1)] hover:text-[var(--ink-1)]',
                ].join(' ');

                return (
                  <button
                    key={item.id}
                    type="button"
                    data-active={active}
                    onClick={() => handleSelectTab(item.id)}
                    className={navClassName}
                  >
                    <Icon
                      kind={item.icon}
                      size={15}
                      stroke={1.9}
                      className={active ? 'text-[var(--success)]' : 'text-[var(--ink-3)]'}
                    />
                    <span className="flex-1 truncate">{item.label}</span>
                    {active ? (
                      <span className="size-1.5 shrink-0 rounded-full bg-[var(--success)]" aria-hidden />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="mt-auto border-t border-[var(--cream-2)] px-5 pt-4">
          <div className="mb-1 flex items-center gap-2">
            <span className="inline-flex size-[7px] shrink-0 rounded-full bg-[var(--success)]" />
            <div className="text-[12px] font-semibold tracking-[-0.005em] text-[var(--ink-1)]">
              {isDirty ? 'Unsaved changes' : 'Live config'}
            </div>
          </div>
          <p className="text-[11px] leading-normal tracking-[-0.005em] text-[var(--ink-3)]">
            {isDirty
              ? 'Save to apply your marketing settings.'
              : 'Changes apply to campaigns and content on save.'}
          </p>
        </div>
      </nav>

      <div className="min-w-0 flex-1 overflow-auto">
        <div className="mx-auto max-w-[920px] px-10 pb-24 pt-8">
          <header className="elva-fade mb-7">
            <div className="mb-1.5 flex items-center gap-2.5">
              <Icon kind="sparkle" size={14} stroke={2} className="text-[var(--ai-3)]" />
              <span className="font-[var(--font-mono)] text-[10px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
                {meta.eyebrow}
              </span>
            </div>
            <h1 className="m-0 font-[var(--font-display)] text-[clamp(28px,3vw,40px)] font-medium leading-[1.15] tracking-[-0.035em] text-[var(--ink-1)]">
              {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
            </h1>
            <p className="mt-4 max-w-[700px] text-[13.5px] leading-normal tracking-[-0.005em] text-[var(--ink-3)]">
              {meta.description}
            </p>
          </header>

          <div className="space-y-6">
            {visibleTab === 'connections' ? (
              <SettingsConnections
                {...(connections ? { connections } : {})}
                {...(onConnect ? { onConnect } : {})}
                {...(onDisconnect ? { onDisconnect } : {})}
              />
            ) : null}
            {visibleTab === 'budget-guardrails' ? (
              <SettingsBudget
                values={budgetValues}
                onChange={handleBudgetChange}
                {...saveFooterProps}
              />
            ) : null}
            {visibleTab === 'practice-profile' ? (
              <SettingsPracticeProfile
                values={profileValues}
                onChange={handleProfileChange}
                {...(doctors ? { doctors } : {})}
                {...saveFooterProps}
              />
            ) : null}
            {visibleTab === 'target-area' ? (
              <SettingsTargetArea
                targetRadiusMiles={targetRadiusMiles}
                competitorScanRadiusKm={competitorScanRadiusKm}
                excludedPostalCodes={excludedPostalCodes}
                onTargetRadiusMilesChange={setTargetRadiusMiles}
                onCompetitorScanRadiusKmChange={setCompetitorScanRadiusKm}
                onExcludedPostalCodesChange={setExcludedPostalCodes}
                {...saveFooterProps}
              />
            ) : null}
            {visibleTab === 'autopilot-rules' ? (
              <SettingsAutopilot
                toggles={autopilotUiToggles}
                onToggleChange={handleAutopilotToggle}
                {...saveFooterProps}
              />
            ) : null}
            {visibleTab === 'notifications' ? (
              <SettingsNotifications
                toggles={notificationUiToggles}
                onToggleChange={handleNotificationToggle}
                {...saveFooterProps}
              />
            ) : null}
            {visibleTab === 'content-preferences' ? (
              <SettingsContent
                selectedTone={selectedTone}
                onSelectTone={setSelectedTone}
                spanishContent={spanishContent}
                onSpanishContentChange={setSpanishContent}
                {...saveFooterProps}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
